/**
 * One acceptance criterion (layers 1 and 2): status, plain-language detail and
 * its evidence.
 *
 * @module features/proofs/components/CriterionCard
 */
import React from 'react';
import { CollapsibleSection, Text } from '@chatsdet/ui';
import { isManualOnly } from '@chatsdet/types';
import type { ProofCriterion, ProofEvidence } from '../types';
import { CheckTypeChips } from './CheckTypeChips';
import { EvidenceGroups } from './EvidenceGroups';
import { ItemStatusBadge } from './ItemStatusBadge';
import { ManualBadge } from './ManualBadge';

type Props = { criterion: ProofCriterion; evidence: ProofEvidence[]; defaultExpanded: boolean };

export const CriterionCard: React.FC<Props> = ({ criterion, evidence, defaultExpanded }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (defaultExpanded) ref.current?.scrollIntoView({ block: 'start' });
  }, [defaultExpanded]);

  return (
    <div ref={ref} id={`criterion-${criterion.position}`} className="scroll-mt-4 px-4 py-1">
      <CollapsibleSection
        hideBorder
        defaultExpanded={defaultExpanded}
        label={`Criterion ${criterion.position}`}
        header={
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <Text weight="semibold">{`${criterion.position}. ${criterion.text}`}</Text>
            {isManualOnly(evidence.map((item) => item.kind)) ? (
              <ManualBadge status={criterion.status} testId="manual-criterion-badge" />
            ) : (
              <ItemStatusBadge status={criterion.status} />
            )}
            <CheckTypeChips evidence={evidence} />
          </div>
        }
        bodyClassName="space-y-2 pl-6"
      >
        {criterion.status.kind === 'failed' && <Text color="destructive">{criterion.status.problem}</Text>}
        {criterion.detail && <Text color="muted">{criterion.detail}</Text>}
        <EvidenceGroups evidence={evidence} forceExpanded={defaultExpanded} />
      </CollapsibleSection>
    </div>
  );
};
